import React, { useState, useEffect } from 'react';
import {
  Lightbulb, TrendingDown, BarChart3, Loader2, AlertTriangle,
  Sparkles, ChevronDown, ChevronUp, Target, ArrowRight
} from 'lucide-react';
import { useQuery } from '@/core/db-adapter/useQuery';
import { quotesApi } from '@/core/adapter-api';
import { useToast, EmptyState, StatsGrid } from './shared';
import {
  calculateProjectSummary,
  calculateProjectWaste,
  identifyVETargets,
  generateVESummary,
  suggestRelatedItems,
  type WasteDetail
} from '../services/ddcSkillsService';
import {
  analyzeBudgetSavings,
  suggestAlternatives,
  getGroqKey
} from '../services/groqService';

// ─── Page ─────────────────────────────────────────────────────────────────────

export const OptimizacionVEPage: React.FC = () => {
  const rawQuotes = useQuery(() => quotesApi.all(), [], ['quotes']) || [];
  const { addToast } = useToast();

  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [expandedTarget, setExpandedTarget] = useState<number | null>(null);
  const [alternativesCache, setAlternativesCache] = useState<Record<string, string>>({});
  const [alternativesLoading, setAlternativesLoading] = useState<string | null>(null);

  const hasGroqKey = !!getGroqKey();

  // Auto-select most recent quote
  useEffect(() => {
    if (rawQuotes.length > 0 && !selectedQuoteId) {
      const sorted = [...rawQuotes].sort((a: any, b: any) => (b.createdAt || 0) - (a.createdAt || 0));
      setSelectedQuoteId(sorted[0].id);
    }
  }, [rawQuotes, selectedQuoteId]);

  const selectedQuote = rawQuotes.find(q => q.id === selectedQuoteId) as any;

  // ── Transform quote lines to budget items ──
  const budgetItems = selectedQuote?.lines?.map((line: any) => ({
    name: line.title || line.description || 'Sin nombre',
    quantity: line.quantity || 1,
    unit: line.unit || 'ud',
    price: line.price || 0,
    total: (line.quantity || 1) * (line.price || 0),
  })) || [];

  // ── DDC Calculations ──
  const projectSummary = budgetItems.length > 0 ? calculateProjectSummary(budgetItems) : null;
  const wasteData = budgetItems.length > 0 ? calculateProjectWaste(budgetItems) : null;
  const veTargets = budgetItems.length > 0 ? identifyVETargets(budgetItems, 10, 1.0) : [];
  const veSummaryText = budgetItems.length > 0 ? generateVESummary(budgetItems) : '';

  const fmtEur = (n: number) => n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });

  // ── AI Analysis ──
  const handleAIAnalysis = async () => {
    if (!hasGroqKey || budgetItems.length === 0) return;
    setAiLoading(true);
    setAiAnalysis(null);
    try {
      const totalExVat = budgetItems.reduce((s: number, i: any) => s + i.total, 0);
      const result = await analyzeBudgetSavings(
        budgetItems.map((i: any) => ({ ...i, unit: i.unit })),
        totalExVat
      );
      setAiAnalysis(result);
    } catch (err: any) {
      addToast('Error al analizar: ' + err.message, 'error');
    } finally {
      setAiLoading(false);
    }
  };

  const handleGetAlternative = async (itemName: string, price: number, unit: string) => {
    if (alternativesCache[itemName]) return;
    setAlternativesLoading(itemName);
    try {
      const result = await suggestAlternatives(itemName, price, unit);
      setAlternativesCache(prev => ({ ...prev, [itemName]: result }));
    } catch (err: any) {
      addToast('Error: ' + err.message, 'error');
    } finally {
      setAlternativesLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/15 rounded-xl">
            <Lightbulb size={28} className="text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Optimización VE</h1>
            <p className="text-sm text-gray-500">Ingeniería de valor · Identifica oportunidades de ahorro</p>
          </div>
        </div>

        {/* Quote Selector */}
        <div className="flex items-center gap-3">
          <select
            value={selectedQuoteId || ''}
            onChange={e => { setSelectedQuoteId(e.target.value); setAiAnalysis(null); }}
            className="bg-[#0d1117] border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-300 focus:border-emerald-500/50 focus:outline-none max-w-[250px]"
          >
            {rawQuotes.length === 0 && <option value="">Sin presupuestos</option>}
            {rawQuotes.map((q: any) => (
              <option key={q.id} value={q.id}>
                {q.number || 'Sin nº'} — {fmtEur(q.grandTotal || q.baseTotal || 0)}
              </option>
            ))}
          </select>
          {hasGroqKey && budgetItems.length > 0 && (
            <button
              onClick={handleAIAnalysis}
              disabled={aiLoading}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500/15 hover:bg-purple-500/25 text-purple-400 border border-purple-500/30 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
            >
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Analizar con IA
            </button>
          )}
        </div>
      </div>

      {budgetItems.length === 0 ? (
        <EmptyState
          icon={Lightbulb}
          title="Sin datos para analizar"
          description="Crea un presupuesto desde 'Presupuestos IA' o importa un BC3 para poder analizar oportunidades de ahorro."
        />
      ) : (
        <div className="space-y-6">
          {/* ── Top Stats ── */}
          {projectSummary && (
            <StatsGrid stats={[
              { label: 'PEM', value: fmtEur(projectSummary.pem) },
              { label: 'PEC (GG+BI)', value: fmtEur(projectSummary.totalPEC) },
              { label: 'Coste Mermas', value: fmtEur(wasteData?.totalWasteCost || 0), color: 'text-orange-400' },
              { label: 'Oportunidades VE', value: `${veTargets.length} partidas`, color: 'text-emerald-400' },
            ]} />
          )}

          {/* ── AI Analysis Result ── */}
          {aiAnalysis && (
            <div className="bg-[#0d1117] border border-purple-500/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-purple-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-purple-400">Análisis IA de Costes (Llama 3.3 70B)</h3>
              </div>
              <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed max-h-[400px] overflow-y-auto">
                {aiAnalysis}
              </div>
            </div>
          )}

          {/* ── VE Targets Table ── */}
          <div className="bg-[#0d1117] border border-white/8 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target size={16} className="text-emerald-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Partidas Objetivo para VE</h3>
              </div>
              <span className="text-[10px] text-gray-600">{veTargets.length} partidas con peso ≥ 1% del PEM</span>
            </div>

            <div className="divide-y divide-white/5">
              {veTargets.map((target, i) => {
                const isExpanded = expandedTarget === i;
                const related = suggestRelatedItems(target.name);
                const altKey = target.name;
                const hasAlt = !!alternativesCache[altKey];
                const isLoadingAlt = alternativesLoading === altKey;
                const pctWidth = Math.min(target.pct * 3, 100);

                return (
                  <div key={i}>
                    <button
                      onClick={() => setExpandedTarget(isExpanded ? null : i)}
                      className={`w-full flex items-center justify-between px-5 py-4 hover:bg-white/3 transition-colors text-left ${isExpanded ? 'bg-emerald-500/5' : ''}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-xs font-bold text-gray-400">
                          #{i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-200 font-medium truncate">{target.name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-gray-500">{fmtEur(target.total)}</span>
                            <div className="w-24 h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${target.pct > 10 ? 'bg-red-500' : target.pct > 5 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                style={{ width: `${pctWidth}%` }}
                              />
                            </div>
                            <span className="text-xs font-bold text-gray-400">{target.pct}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold flex-shrink-0">{target.veOpportunity}</span>
                        {isExpanded ? <ChevronUp size={14} className="text-gray-500" /> : <ChevronDown size={14} className="text-gray-500" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-5 pb-4 bg-[#0a0e14] border-t border-white/5 space-y-3">
                        {/* Related items */}
                        {related.length > 0 && (
                          <div className="pt-3">
                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Partidas relacionadas a considerar</p>
                            <div className="flex flex-wrap gap-2">
                              {related.map((r, j) => (
                                <span key={j} className="px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-400">
                                  {r}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* AI alternatives */}
                        {hasGroqKey && (
                          <div className="pt-2">
                            {!hasAlt && !isLoadingAlt && (
                              <button
                                onClick={() => handleGetAlternative(target.name, target.total, 'ud')}
                                className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 rounded-lg text-xs font-semibold transition-colors"
                              >
                                <Sparkles size={12} />
                                Pedir alternativas a IA
                              </button>
                            )}
                            {isLoadingAlt && (
                              <div className="flex items-center gap-2 text-gray-400 text-xs py-2">
                                <Loader2 size={14} className="animate-spin text-purple-400" />
                                Consultando Llama 3.3…
                              </div>
                            )}
                            {hasAlt && (
                              <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-3">
                                <p className="text-[10px] uppercase tracking-wider text-purple-400 font-bold mb-2">Alternativas sugeridas por IA</p>
                                <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{alternativesCache[altKey]}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Waste Summary ── */}
          {wasteData && wasteData.totalWasteCost > 0 && (
            <div className="bg-[#0d1117] border border-orange-500/15 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingDown size={16} className="text-orange-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-orange-400">Resumen de Mermas</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-[#010409] rounded-lg p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Coste extra por mermas</p>
                  <p className="text-2xl font-bold text-orange-400 mt-1">{fmtEur(wasteData.totalWasteCost)}</p>
                </div>
                <div className="bg-[#010409] rounded-lg p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">% medio de merma</p>
                  <p className="text-2xl font-bold text-orange-300 mt-1">{wasteData.avgWastePct}%</p>
                </div>
                <div className="bg-[#010409] rounded-lg p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Materiales analizados</p>
                  <p className="text-2xl font-bold text-gray-200 mt-1">{wasteData.items.length}</p>
                </div>
              </div>
              {/* Top waste items */}
              <div className="mt-4 space-y-2">
                {wasteData.items
                  .filter(w => w.wasteCost > 0)
                  .sort((a, b) => b.wasteCost - a.wasteCost)
                  .slice(0, 5)
                  .map((w, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#010409] rounded-lg px-4 py-2">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-orange-500/10 text-orange-400 border border-orange-500/20 flex-shrink-0">
                          {w.materialType}
                        </span>
                        <span className="text-sm text-gray-300 truncate">{w.totalWastePct}% merma</span>
                      </div>
                      <span className="text-sm font-bold text-orange-400 ml-3">{fmtEur(w.wasteCost)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* ── VE Summary (Markdown-ish) ── */}
          {!hasGroqKey && veSummaryText && (
            <div className="bg-[#0d1117] border border-white/8 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 size={16} className="text-amber-400" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Resumen VE generado</h3>
              </div>
              <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed font-mono bg-[#010409] rounded-lg p-4 max-h-[300px] overflow-y-auto">
                {veSummaryText}
              </div>
            </div>
          )}

          {/* Groq upsell */}
          {!hasGroqKey && (
            <div className="bg-[#0d1117] border border-purple-500/15 rounded-xl p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <Sparkles size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-200">¿Quieres análisis IA gratuito?</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configura tu API key de Groq (gratuita) en Configuración para obtener análisis de costes con Llama 3.3 70B y sugerencias de alternativas por partida.
                </p>
              </div>
              <a
                href="#/configuracion-obra"
                className="flex items-center gap-1 px-4 py-2 bg-purple-500/15 text-purple-400 border border-purple-500/30 rounded-lg text-xs font-bold hover:bg-purple-500/25 transition-colors flex-shrink-0"
              >
                Configurar <ArrowRight size={12} />
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
